// Changelog der App. Web und Mobile zeigen beim Start alle Releases, die das Gerät
// noch nicht gesehen hat, als "Was ist neu" an. Der Stand wird lokal gespeichert.
//
// Neue Releases oben anfügen. Die id ist das Veröffentlichungsdatum (YYYY-MM-DD),
// muss eindeutig sein und entscheidet per String-Vergleich, was neuer ist als der
// gespeicherte Stand. Zwei Releases am selben Tag: Einträge zusammenlegen.

export type ChangelogPlatform = 'web' | 'mobile';
export type ChangelogItemKind = 'new' | 'improved' | 'fixed';

export interface ChangelogItem {
  kind: ChangelogItemKind;
  title: string;
  description: string;
  /** Ohne Angabe gilt der Eintrag für Web und Mobile. */
  platforms?: readonly ChangelogPlatform[];
}

export interface ChangelogRelease {
  /** Veröffentlichungsdatum als YYYY-MM-DD, eindeutig und sortierbar. */
  id: string;
  title: string;
  items: readonly ChangelogItem[];
}

export const CHANGELOG_KIND_LABELS: Record<ChangelogItemKind, string> = {
  new: 'Neu',
  improved: 'Verbessert',
  fixed: 'Behoben',
};

export const CHANGELOG: readonly ChangelogRelease[] = [
  {
    id: '2026-08-24',
    title: 'Kommentare, Eventsuche und ein klarerer Feed',
    items: [
      {
        kind: 'new',
        title: 'Kommentare zu News-Beiträgen',
        description:
          'Du kannst Beiträge kommentieren, auf Kommentare antworten und sie mit einem Herz markieren. Eigene Kommentare lassen sich eine Stunde lang bearbeiten.',
      },
      {
        kind: 'new',
        title: 'Eventsuche',
        description: 'Events findest du jetzt über das Suchfeld nach Namen.',
      },
      {
        kind: 'improved',
        title: 'Filter und Sortierung auf der Startseite',
        description:
          'Die Feed-Filter zeigen, wie viele Einträge dahinterstecken. Events sortierst du nach nächstem Termin oder nach Erstellungsdatum.',
        platforms: ['mobile'],
      },
    ],
  },
];

function compareReleaseIdsDesc(a: ChangelogRelease, b: ChangelogRelease): number {
  if (a.id === b.id) return 0;
  return a.id < b.id ? 1 : -1;
}

/** Releases mit den Einträgen für eine Plattform, neueste zuerst. Releases ohne passende Einträge entfallen. */
export function getReleasesForPlatform(platform: ChangelogPlatform): ChangelogRelease[] {
  return CHANGELOG.map((release) => ({
    ...release,
    items: release.items.filter((item) => !item.platforms || item.platforms.includes(platform)),
  }))
    .filter((release) => release.items.length > 0)
    .sort(compareReleaseIdsDesc);
}

/**
 * Noch nicht gesehene Releases, neueste zuerst.
 * Ohne gespeicherten Stand (Erstinstallation oder erster Start nach Einführung) nur das neueste Release.
 */
export function getUnseenReleases(platform: ChangelogPlatform, lastSeenReleaseId: string | null): ChangelogRelease[] {
  const releases = getReleasesForPlatform(platform);
  if (!lastSeenReleaseId) {
    const latest = releases[0];
    return latest ? [latest] : [];
  }
  return releases.filter((release) => release.id > lastSeenReleaseId);
}

/** "24. August 2026" aus der Release-id; fällt auf die id zurück, wenn sie kein Datum ist. */
export function formatReleaseDate(releaseId: string): string {
  const [year, month, day] = releaseId.split('-').map(Number);
  if (!year || !month || !day) return releaseId;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return releaseId;
  return date.toLocaleDateString('de-CH', { day: 'numeric', month: 'long', year: 'numeric' });
}
